import Alpine from 'alpinejs';

export interface IStudent {
  studentDob: string;
  studentFirstname: string;
  studentLastname: string;
  studentYear: string;
  variants: {
    variantId: number;
    price: number;
    relatedVariantId: number | undefined;
  }[];
}

export interface IVariant {
  variant: string;
  title: string;
  year: string;
  mode: string;
  price: string;
}

export interface ISchoolInfo {
  addressLine1: string;
  addressLine2: string;
  collectionUrl: string;
  countryName: string;
  postCode: string;
  schoolCode: string;
  schoolName: string;
  stateName: string;
  suburb: string;
}

export interface IStudentList {
  dob: string;
  firstname: string;
  lastname: string;
  year: string;
  variants: number[];
}

interface ILevyConfig {
  enabled: boolean;
  variantId: number | null;
}

interface ICartLineItem {
  id: number;
  quantity: number;
  properties?: Record<string, string>;
}

Alpine.data('selectSubjects', () => ({
  loading: false,
  hasErrors: false,
  students: [] as IStudent[],
  accessCodes: [],
  total: 0,
  totalPrice: 0,
  schoolInfo: {} as ISchoolInfo,
  init() {},
  async clearStudentCart() {
    const updates: { [key: string]: number } = {};

    const response = await fetch('/cart.json');
    const cart = await response.json();

    for (const cartItem of cart.items) {
      if (cartItem.properties?._origin != undefined) {
        updates[cartItem.key as string] = 0;
      }
    }
    if (Object.keys(updates).length) {
      return fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
    }
  },

  async processStudentList() {
    this.students = [];
    this.schoolInfo = window.ppsSchoolDetails as ISchoolInfo;

    const studentList = window.studentList as Record<string, IStudentList>;
    const studentListKeyValues = Object.entries(studentList);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    studentListKeyValues.forEach(([_key, studentListItem]) => {
      this.students.push(studentListItem);
      this.total += studentListItem.variants.length;
    });
  },

  getLevyConfig(): ILevyConfig {
    const collection = (window as any).collection;
    return collection?.levy ?? { enabled: false, variantId: null };
  },

  async handleSubmit(e: Event) {
    e.preventDefault();
    this.hasErrors = false;
    this.loading = true;

    try {
      await this.clearStudentCart();
      await this.processStudentList();

      // The School Program Support Fee is added once per student by the janison-ecommerce-app cart transform,
      // which looks for _levy_enabled on the student's tests.
      const levy = this.getLevyConfig();
      const levyEnabled = levy.enabled && Boolean(levy.variantId);
      const assessmentItems: ICartLineItem[] = [];
      const relatedVariantItems: ICartLineItem[] = [];

      this.students.forEach((s) => {
        const student = s as IStudent;

        student.variants.forEach((variant) => {
          const properties = {
            student_firstname: student.studentFirstname,
            student_lastname: student.studentLastname,
            student_dob: student.studentDob,
            student_year: student.studentYear,
            school_name: this.schoolInfo.schoolName,
            _school_code: this.schoolInfo.schoolCode,
            _school_name: this.schoolInfo.schoolName,
            _origin: 'parent',
            _address_line_1: this.schoolInfo.addressLine1,
            _address_line_2: this.schoolInfo.addressLine2,
            _suburb: this.schoolInfo.suburb,
            _country_name: this.schoolInfo.countryName,
            _state_name: this.schoolInfo.stateName,
            _post_code: this.schoolInfo.postCode,
            ...(levyEnabled && { _levy_enabled: 'true' }),
          };

          if (!variant?.variantId) {
            console.warn('Invalid variantId for student', student.studentFirstname, student.studentLastname);
            return;
          }

          // Add the assessment item
          assessmentItems.push({ id: variant.variantId, quantity: 1, properties: properties });

          if (variant.relatedVariantId) {
            relatedVariantItems.push({
              id: variant.relatedVariantId,
              quantity: 1,
              properties: {
                _origin: 'parent',
              },
            });
          }
        });
      });

      // Add assessment items first
      const assessmentResponse = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: assessmentItems }),
      });

      if (!assessmentResponse.ok) {
        this.hasErrors = true;
        return;
      }

      // Add related variant items (bundles)
      if (relatedVariantItems.length > 0) {
        await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: relatedVariantItems }),
        });
      }

      window.location.href = '/cart';
    } catch (e) {
      console.error(e);
      this.hasErrors = true;
    } finally {
      this.loading = false;
    }
  },
}));
